// src/components/AdditionalDocumentGeneration.tsx

import {
  BuildOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  LoadingOutlined,
  NodeExpandOutlined,
  OrderedListOutlined,
  StarOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Modal,
  Progress,
  Row,
  Select,
  Statistic,
  Steps,
  Tooltip,
  Typography,
} from "antd";
import { AnimatePresence, motion } from "framer-motion";
import {
  BookOpenCheck as BookOpenCheckIcon,
  BookOpenText as BookOpenTextIcon,
  Brain as BrainIcon,
  CheckSquare as CheckSquareIcon,
  FileText as FileTextIcon,
  GraduationCap as GraduationCapIcon,
  Layers as LayersIcon,
  Loader2 as Loader2Icon,
  Repeat as RepeatIcon,
} from "lucide-react";
import Image from "next/image";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR, { mutate } from "swr";
import { withErrorBoundary } from "@/components/ErrorBoundary";
import { notify } from "@/components/Notification";
import useAppStore from "@/store/useAppStore";
import type {
  DocumentOptionKey,
  FastApiJobSummaryResponse,
  FileData,
  ProcessingJob,
  UserInfo,
} from "@/types";
import { safeStringify } from "@/utils/api";
import { calculateCostBreakdown } from "@/utils/documentUtils";
import { trackEvent } from "@/utils/gtm";
import AudienceSelector from "./AudienceSelector";
import ConfirmationModal from "./ConfirmationModal";
import CostBreakdownProgress from "./CostBreakdownProgress";
import ProcessedDocumentResultsViewer from "./ProcessedDocumentResultsViewer";

const { Title, Text } = Typography;

const ErrorFallback = (
  <Alert
    message="Something went wrong"
    description="There was an error loading this component. Please try refreshing the page."
    type="error"
    showIcon
    className="mb-4"
  />
);

interface DocumentOption {
  key: DocumentOptionKey;
  title: string;
  icon: React.ComponentType<any>;
  description: string;
  color: string;
}

interface AdditionalDocGenerationProps {
  job: ProcessingJob;
  onClose: () => void;
  onGenerated?: () => void;
  userInfo: UserInfo | null;
  documentInfo: {
    originalFileName: string;
    originalFileSize: number;
    totalPages: number;
  } | null;
}

interface JobSummary {
  job_id: string;
  document_id: string;
  status: string;
  progress_percentage: number;
  update_time: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  page_count: number;
  word_count: number;
  processing_time: number;
  total_input_tokens: number;
  total_output_tokens: number;
  processing_cost: number;
  optimization_cost: number;
  generation_flags: {
    requested: Record<string, boolean>;
    completed: Record<string, boolean>;
  };
  token_usage: Record<string, { input_tokens: number; output_tokens: number }>;
  [key: string]: any;
}

const fetcher = async (url: string) => {
  const res = await fetch(url);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Failed to fetch");
  }
  return res.json();
};

const getIconForType = (iconType: string) => {
  switch (iconType) {
    case "build":
      return <BuildOutlined />;
    case "orderedList":
      return <OrderedListOutlined />;
    case "nodeExpand":
      return <NodeExpandOutlined />;
    case "checkCircle":
      return <CheckCircleOutlined />;
    default:
      return <FileTextOutlined />;
  }
};

const AdditionalDocumentGeneration: React.FC<AdditionalDocGenerationProps> = ({
  job,
  onClose,
  onGenerated,
  userInfo,
  documentInfo,
}) => {
  const progressTrackerRef = useRef<HTMLDivElement>(null);
  const resultsSectionRef = useRef<HTMLDivElement>(null);

  const [showResultsViewer, setShowResultsViewer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [processingFiles, setProcessingFiles] = useState(false);
  const [generationSuccessful, setGenerationSuccessful] = useState(false);
  const [creditCost, setCreditCost] = useState<number | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [initialDocTypes, setInitialDocTypes] = useState<DocumentOptionKey[]>([]);
  const [customLessonPlanTargetAudience, setCustomLessonPlanTargetAudience] = useState<string>("");
  const [lessonPlanTargetAudience, setLessonPlanTargetAudience] = useState<string>(
    "American high school students between the ages of 15 and 18 with average scholastic aptitude",
  );
  const [presentationTheme, setPresentationTheme] = useState<string>("black");
  const [lastNewlyGeneratedDocFilename, setLastNewlyGeneratedDocFilename] = useState<string | null>(
    null,
  );

  const [options, setOptions] = useState<Record<DocumentOptionKey, boolean>>({
    generateMultipleChoiceQuiz: false,
    generateShortAnswerQuiz: false,
    generateAnkiFlashcards: false,
    generatePresentationSlides: false,
    generateSummary: false,
    generateLessonPlan: false,
    generateMindmap: false,
    generateReadabilityAnalysis: false,
  });

  const initialSetupDone = useRef(false);

  const {
    additionalGeneration: {
      isGeneratingAdditional,
      additionalGenerationProgress,
      additionalGenerationStatus,
      additionalGenerationError,
      existingDocumentTypes,
      stages,
      optimization_qualityScore,
      inputTokens,
      outputTokens,
      lastProcessed,
      lastOptimized,
      statusDescription,
      additionalGenerationJobId,
    },
    updateGenerationStages,
    pollAdditionalGenerationStatus,
    startAdditionalGeneration,
    setCurrentJobId,
  } = useAppStore();

  const presentationThemes = useMemo(
    () => [
      { name: "black", label: "Black", imgSrc: "/presentation_theme_sample_images/black.png" },
      { name: "white", label: "White", imgSrc: "/presentation_theme_sample_images/white.png" },
      { name: "league", label: "League", imgSrc: "/presentation_theme_sample_images/league.png" },
      { name: "beige", label: "Beige", imgSrc: "/presentation_theme_sample_images/beige.png" },
      { name: "night", label: "Night", imgSrc: "/presentation_theme_sample_images/night.png" },
      { name: "moon", label: "Moon", imgSrc: "/presentation_theme_sample_images/moon.png" },
      { name: "serif", label: "Serif", imgSrc: "/presentation_theme_sample_images/serif.png" },
      { name: "simple", label: "Simple", imgSrc: "/presentation_theme_sample_images/simple.png" },
      { name: "sky", label: "Sky", imgSrc: "/presentation_theme_sample_images/sky.png" },
      {
        name: "solarized",
        label: "Solarized",
        imgSrc: "/presentation_theme_sample_images/solarized.png",
      },
      {
        name: "dracula",
        label: "Dracula",
        imgSrc: "/presentation_theme_sample_images/dracula.png",
      },
      { name: "blood", label: "Blood", imgSrc: "/presentation_theme_sample_images/blood.png" },
    ],
    [],
  );

  const documentOptions: DocumentOption[] = useMemo(
    () => [
      {
        key: "generateMultipleChoiceQuiz",
        title: "Multiple Choice Quiz",
        icon: CheckSquareIcon,
        description: "Generate an interactive, gradable multiple-choice quiz.",
        color: "text-blue-500",
      },
      {
        key: "generateShortAnswerQuiz",
        title: "Short Answer Quiz",
        icon: BookOpenTextIcon,
        description: "Generate a short-answer quiz.",
        color: "text-yellow-500",
      },
      {
        key: "generateAnkiFlashcards",
        title: "Anki Flashcards",
        icon: RepeatIcon,
        description: "Create Anki flashcards for memorization.",
        color: "text-purple-500",
      },
      {
        key: "generatePresentationSlides",
        title: "Presentation Slides",
        icon: LayersIcon,
        description: "Create presentation slides summarizing key points.",
        color: "text-orange-500",
      },
      {
        key: "generateSummary",
        title: "Summary",
        icon: FileTextIcon,
        description: "Generate a comprehensive summary of the document.",
        color: "text-teal-500",
      },
      {
        key: "generateMindmap",
        title: "Mindmap",
        icon: BrainIcon,
        description: "Generate a mindmap/outline.",
        color: "text-indigo-500",
      },
      {
        key: "generateReadabilityAnalysis",
        title: "Readability Analysis",
        icon: BookOpenCheckIcon,
        description: "Generate a readability analysis.",
        color: "text-green-500",
      },
      {
        key: "generateLessonPlan",
        title: "Lesson Plan",
        icon: GraduationCapIcon,
        description: "Create a structured lesson plan.",
        color: "text-brown-500",
      },
    ],
    [],
  );

  useEffect(() => {
    updateGenerationStages(options);
  }, [options, updateGenerationStages]);

  const {
    data: jobSummaryData,
    error: jobSummaryError,
    mutate: mutateJobSummary,
    isValidating: isValidatingJobSummary,
  } = useSWR<JobSummary>(
    job.job_id ? `/api/document_job_summary/${encodeURIComponent(job.job_id)}` : null,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 0,
      refreshInterval: 2000,
    },
  );

  useEffect(() => {
    if (jobSummaryError) {
      console.error("Error fetching job summary:", safeStringify(jobSummaryError));
      setError("Failed to fetch job summary data");
    }
  }, [jobSummaryError]);

  useEffect(() => {
    if (jobSummaryData && !initialSetupDone.current) {
      try {
        const generationFlags = jobSummaryData.generation_flags;
        if (!generationFlags || !generationFlags.completed || !generationFlags.requested) {
          console.error("Invalid generation flags format:", safeStringify(generationFlags));
          setError("Invalid generation flags format from server");
          return;
        }

        setDocumentId(jobSummaryData.document_id);

        const completedFlags = generationFlags.completed;

        const generatedTypes = Object.entries(completedFlags)
          .filter(([_, completed]) => completed === true)
          .map(([key]) => {
            const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
            return camelKey.startsWith("generate")
              ? (camelKey as DocumentOptionKey)
              : (("generate" +
                  camelKey.charAt(0).toUpperCase() +
                  camelKey.slice(1)) as DocumentOptionKey);
          });

        const updatedOptions = { ...options };

        generatedTypes.forEach((type) => {
          if (type in updatedOptions) {
            updatedOptions[type] = true;
          }
        });

        setOptions(updatedOptions);
        setInitialDocTypes(generatedTypes);
        useAppStore.setState((state) => ({
          ...state,
          additionalGeneration: {
            ...state.additionalGeneration,
            existingDocumentTypes: generatedTypes,
          },
        }));

        initialSetupDone.current = true;
      } catch (err) {
        console.error("Error processing job summary data:", safeStringify(err));
        setError("Failed to process job summary data");
      }
    }
  }, [jobSummaryData, options]);

  useEffect(() => {
    if (Array.isArray(existingDocumentTypes) && existingDocumentTypes.length > 0) {
      setOptions((prev) => {
        const newOptions = { ...prev };
        existingDocumentTypes.forEach((type) => {
          newOptions[type] = true;
        });
        return newOptions;
      });
    }
  }, [existingDocumentTypes]);

  useEffect(() => {
    if (isGeneratingAdditional && progressTrackerRef.current) {
      const timeoutId = setTimeout(() => {
        progressTrackerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isGeneratingAdditional]);

  useEffect(() => {
    if (generationSuccessful && showResultsViewer && resultsSectionRef.current) {
      resultsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [generationSuccessful, showResultsViewer]);

  const newlyGeneratedDocs = useMemo(() => {
    if (!generationSuccessful || !Array.isArray(existingDocumentTypes)) return [];
    return existingDocumentTypes.filter((type) => !initialDocTypes.includes(type));
  }, [generationSuccessful, existingDocumentTypes, initialDocTypes]);

  useEffect(() => {
    const checkCompletion = async () => {
      const currentState = useAppStore.getState().additionalGeneration;
      const timeSinceStart = currentState.processingStartTime
        ? Date.now() - currentState.processingStartTime
        : 0;
      const MAX_PROCESSING_TIME = 15 * 60 * 1000; // 15 minutes

      // If stuck for too long
      if (currentState.isGeneratingAdditional && timeSinceStart > MAX_PROCESSING_TIME) {
        console.error("Generation appears stuck - resetting state");
        useAppStore.setState((state) => ({
          ...state,
          additionalGeneration: {
            ...state.additionalGeneration,
            isGeneratingAdditional: false,
            additionalGenerationStatus: "failed",
            additionalGenerationError: "Generation timed out - please try again",
            additionalGenerationProgress: 0,
            showResults: false,
            keepModalOpen: false,
            stages: state.additionalGeneration.stages.map((stage) => ({
              ...stage,
              isCompleted: false,
            })),
            optimization_qualityScore: null,
            inputTokens: null,
            outputTokens: null,
            lastProcessed: null,
            lastOptimized: null,
            statusDescription: "Generation timed out - please try again",
          },
        }));
        notify.error({ content: "Generation timed out - please try again" });
        return;
      }

      if (!jobSummaryData || !isGeneratingAdditional) {
        return;
      }

      // Error state detection
      const failed =
        jobSummaryData.status === "processing_failed" ||
        jobSummaryData.status.endsWith("_failed") ||
        jobSummaryData.status.includes("generation_failed") ||
        jobSummaryData.status === "failed";
      if (failed || jobSummaryData.error_message) {
        useAppStore.setState((state) => ({
          ...state,
          additionalGeneration: {
            ...state.additionalGeneration,
            isGeneratingAdditional: false,
            additionalGenerationStatus: "failed",
            additionalGenerationError: jobSummaryData.error_message || "Generation failed",
            additionalGenerationProgress: 0,
            showResults: false,
            keepModalOpen: false,
            stages: state.additionalGeneration.stages.map((stage) => ({
              ...stage,
              isCompleted: false,
            })),
            optimization_qualityScore: null,
            inputTokens: null,
            outputTokens: null,
            lastProcessed: null,
            lastOptimized: null,
            statusDescription: jobSummaryData.error_message || "Generation failed",
          },
        }));
        notify.error({ content: jobSummaryData.error_message || "Generation failed" });
        return;
      }

      if (!isValidatingJobSummary) {
        const { status, generation_flags } = jobSummaryData;
        const currentState = useAppStore.getState().additionalGeneration;

        // Wait for actual processing state
        if (!currentState.processingStartTime) {
          return;
        }

        const isProcessingState =
          status.includes("processing") ||
          status.includes("generating") ||
          status.includes("starting");

        if (isProcessingState && !currentState.hasSeenProcessingState) {
          useAppStore.setState((state) => ({
            ...state,
            additionalGeneration: {
              ...state.additionalGeneration,
              hasSeenProcessingState: true,
            },
          }));
        }

        if (status !== "completed" && status !== "failed") {
          useAppStore.setState((state) => ({
            ...state,
            additionalGeneration: {
              ...state.additionalGeneration,
              additionalGenerationProgress: jobSummaryData.progress_percentage || 0,
              additionalGenerationStatus: status,
              optimization_qualityScore: jobSummaryData.optimization_quality_score || null,
              inputTokens: jobSummaryData.total_input_tokens || null,
              outputTokens: jobSummaryData.total_output_tokens || null,
              lastProcessed: jobSummaryData.last_processed || null,
              lastOptimized: jobSummaryData.last_optimized || null,
              statusDescription: `Processing - ${jobSummaryData.progress_percentage || 0}% complete`,
              lastKnownGenerationFlags: generation_flags,
            },
          }));
        }

        if (generation_flags && generation_flags.requested && generation_flags.completed) {
          const allRequestedComplete = Object.entries(generation_flags.requested)
            .filter(([key, requested]) => {
              const isNewRequest = !initialDocTypes.includes(key as DocumentOptionKey);
              return requested && isNewRequest;
            })
            .every(([key]) => generation_flags.completed[key]);

          const timeSinceStart = Date.now() - currentState.processingStartTime;
          const hasWaitedLongEnough = timeSinceStart > 10000; // 10 second minimum

          if (
            allRequestedComplete &&
            !generationSuccessful &&
            newlyGeneratedDocs.length > 0 &&
            currentState.hasSeenProcessingState &&
            hasWaitedLongEnough
          ) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            setGenerationSuccessful(true);
            setShowResultsViewer(true);

            useAppStore.setState((state) => ({
              ...state,
              additionalGeneration: {
                ...state.additionalGeneration,
                isGeneratingAdditional: false,
                additionalGenerationProgress: 100,
                additionalGenerationStatus: "completed",
                showResults: true,
                keepModalOpen: true,
                stages: state.additionalGeneration.stages.map((stage) => ({
                  ...stage,
                  isCompleted: true,
                })),
                optimization_qualityScore: jobSummaryData.optimization_quality_score || null,
                inputTokens: jobSummaryData.total_input_tokens || null,
                outputTokens: jobSummaryData.total_output_tokens || null,
                lastProcessed: jobSummaryData.last_processed || null,
                lastOptimized: jobSummaryData.last_optimized || null,
                statusDescription: "Generation completed successfully",
                lastKnownGenerationFlags: generation_flags,
              },
            }));
          }
        }
      }
    };

    checkCompletion().catch((error) => {
      console.error("Error in checkCompletion:", error);
      useAppStore.setState((state) => ({
        ...state,
        additionalGeneration: {
          ...state.additionalGeneration,
          isGeneratingAdditional: false,
          additionalGenerationStatus: "failed",
          additionalGenerationError:
            error instanceof Error ? error.message : "Error checking completion status",
          additionalGenerationProgress: 0,
          showResults: false,
          keepModalOpen: false,
          stages: state.additionalGeneration.stages.map((stage) => ({
            ...stage,
            isCompleted: false,
          })),
          optimization_qualityScore: null,
          inputTokens: null,
          outputTokens: null,
          lastProcessed: null,
          lastOptimized: null,
          statusDescription:
            error instanceof Error ? error.message : "Error checking completion status",
        },
      }));
      setError(error instanceof Error ? error.message : "Error checking completion status");
    });
  }, [
    jobSummaryData,
    isValidatingJobSummary,
    showResultsViewer,
    isGeneratingAdditional,
    initialDocTypes,
    newlyGeneratedDocs.length,
    generationSuccessful,
  ]);

  const handleOptionChange = useCallback(
    (option: DocumentOptionKey, checked: boolean) => {
      if (!Array.isArray(existingDocumentTypes)) {
        console.debug("existingDocumentTypes is not an array");
        return;
      }
      if (existingDocumentTypes.includes(option)) return;

      setOptions((prev) => ({ ...prev, [option]: checked }));

      if (documentInfo?.totalPages) {
        const newOptions = { ...options, [option]: checked };
        const costBreak = calculateCostBreakdown(0, newOptions);
        setCreditCost(costBreak.totalCost);
      }
    },
    [options, existingDocumentTypes, documentInfo?.totalPages],
  );

  const getSelectedOptions = useCallback(
    () => ({
      generateMultipleChoiceQuiz:
        options.generateMultipleChoiceQuiz &&
        !existingDocumentTypes.includes("generateMultipleChoiceQuiz"),
      generateShortAnswerQuiz:
        options.generateShortAnswerQuiz &&
        !existingDocumentTypes.includes("generateShortAnswerQuiz"),
      generateAnkiFlashcards:
        options.generateAnkiFlashcards && !existingDocumentTypes.includes("generateAnkiFlashcards"),
      generatePresentationSlides:
        options.generatePresentationSlides &&
        !existingDocumentTypes.includes("generatePresentationSlides"),
      generateSummary:
        options.generateSummary && !existingDocumentTypes.includes("generateSummary"),
      generateMindmap:
        options.generateMindmap && !existingDocumentTypes.includes("generateMindmap"),
      generateReadabilityAnalysis:
        options.generateReadabilityAnalysis &&
        !existingDocumentTypes.includes("generateReadabilityAnalysis"),
      generateLessonPlan:
        options.generateLessonPlan && !existingDocumentTypes.includes("generateLessonPlan"),
      lessonPlanTargetAudience: options.generateLessonPlan
        ? lessonPlanTargetAudience === "custom"
          ? customLessonPlanTargetAudience
          : lessonPlanTargetAudience
        : undefined,
      presentationTheme: options.generatePresentationSlides ? presentationTheme : undefined,
    }),
    [
      options,
      existingDocumentTypes,
      lessonPlanTargetAudience,
      customLessonPlanTargetAudience,
      presentationTheme,
    ],
  );

  const calculateIncrementalCost = useCallback(() => {
    const newOptions = {
      generateMultipleChoiceQuiz:
        options.generateMultipleChoiceQuiz &&
        !existingDocumentTypes.includes("generateMultipleChoiceQuiz"),
      generateShortAnswerQuiz:
        options.generateShortAnswerQuiz &&
        !existingDocumentTypes.includes("generateShortAnswerQuiz"),
      generateAnkiFlashcards:
        options.generateAnkiFlashcards && !existingDocumentTypes.includes("generateAnkiFlashcards"),
      generatePresentationSlides:
        options.generatePresentationSlides &&
        !existingDocumentTypes.includes("generatePresentationSlides"),
      generateSummary:
        options.generateSummary && !existingDocumentTypes.includes("generateSummary"),
      generateMindmap:
        options.generateMindmap && !existingDocumentTypes.includes("generateMindmap"),
      generateReadabilityAnalysis:
        options.generateReadabilityAnalysis &&
        !existingDocumentTypes.includes("generateReadabilityAnalysis"),
      generateLessonPlan:
        options.generateLessonPlan && !existingDocumentTypes.includes("generateLessonPlan"),
    };
    return calculateCostBreakdown(0, newOptions);
  }, [options, existingDocumentTypes]);

  const handleSubmit = async () => {
    try {
      const selectedOptions = getSelectedOptions();
      const hasSelectedOptions = Object.values(selectedOptions).some((value) => value === true);

      if (!hasSelectedOptions) {
        throw new Error("Please select at least one document type to generate");
      }

      if (options.generateLessonPlan) {
        if (!lessonPlanTargetAudience) {
          throw new Error("Please select a target audience for the lesson plan");
        }
        if (lessonPlanTargetAudience === "custom" && !customLessonPlanTargetAudience.trim()) {
          throw new Error("Please provide a description for your custom target audience");
        }
      }

      if (options.generatePresentationSlides && !presentationTheme) {
        throw new Error("Please select a presentation theme");
      }

      const newOptions = {
        generateMultipleChoiceQuiz:
          options.generateMultipleChoiceQuiz &&
          !existingDocumentTypes.includes("generateMultipleChoiceQuiz"),
        generateShortAnswerQuiz:
          options.generateShortAnswerQuiz &&
          !existingDocumentTypes.includes("generateShortAnswerQuiz"),
        generateAnkiFlashcards:
          options.generateAnkiFlashcards &&
          !existingDocumentTypes.includes("generateAnkiFlashcards"),
        generatePresentationSlides:
          options.generatePresentationSlides &&
          !existingDocumentTypes.includes("generatePresentationSlides"),
        generateSummary:
          options.generateSummary && !existingDocumentTypes.includes("generateSummary"),
        generateMindmap:
          options.generateMindmap && !existingDocumentTypes.includes("generateMindmap"),
        generateReadabilityAnalysis:
          options.generateReadabilityAnalysis &&
          !existingDocumentTypes.includes("generateReadabilityAnalysis"),
        generateLessonPlan:
          options.generateLessonPlan && !existingDocumentTypes.includes("generateLessonPlan"),
      };

      const costBreak = calculateCostBreakdown(0, newOptions);
      setCreditCost(costBreak.totalCost);

      if (costBreak.totalCost > (userInfo?.availableCredits || 0)) {
        throw new Error("Insufficient credits available");
      }

      setShowConfirmation(true);
    } catch (error: any) {
      setError(error.message);
      notify.error({ content: error.message });
    }
  };

  const handleConfirm = async () => {
    setShowConfirmation(false);
    setError(null);
    setLoading(true);

    useAppStore.setState((state) => ({
      ...state,
      additionalGeneration: {
        ...state.additionalGeneration,
        isGeneratingAdditional: true,
        additionalGenerationStatus: "starting",
        additionalGenerationProgress: 0,
        hasSeenProcessingState: false,
        processingStartTime: null,
        showResults: false,
        keepModalOpen: true,
        lastKnownGenerationFlags: null,
        statusTransitions: [],
        optimization_qualityScore: null,
        inputTokens: null,
        outputTokens: null,
        lastProcessed: null,
        lastOptimized: null,
        statusDescription: "Starting additional document generation...",
      },
    }));

    try {
      const selectedOptions = getSelectedOptions();
      if (!documentId) {
        throw new Error("Document ID is missing. Please try again.");
      }

      await startAdditionalGeneration(job.job_id, selectedOptions);

      useAppStore.setState((state) => ({
        ...state,
        additionalGeneration: {
          ...state.additionalGeneration,
          additionalGenerationJobId: job.job_id,
          additionalGenerationStatus: "starting",
          additionalGenerationProgress: 0,
          hasSeenProcessingState: false,
          processingStartTime: Date.now(),
          showResults: false,
          keepModalOpen: true,
        },
      }));

      setError(null);
      setCurrentJobId(job.job_id);
    } catch (error: any) {
      console.error("Error starting generation:", safeStringify(error));
      const errorMessage =
        error.response?.data?.error || error.message || "Failed to start generation";
      setError(errorMessage);
      notify.error({ content: errorMessage });

      trackEvent("additional_documents_error", {
        document_id: job.document_id,
        job_id: job.job_id,
        error: errorMessage,
        status: error.response?.status,
      });

      useAppStore.setState((state) => ({
        ...state,
        additionalGeneration: {
          ...state.additionalGeneration,
          isGeneratingAdditional: false,
        },
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleFilesGenerated = useCallback(
    async (updatedFiles: FileData[]) => {
      try {
        setProcessingFiles(true);

        const normalizeStatus = (status: string): string => {
          return status.endsWith("_") ? status.slice(0, -1) : status;
        };

        const { data: currentJobs } = (await fetch("/api/processing_jobs").then((res) =>
          res.json(),
        )) as { data: ProcessingJob[] };

        const statusUpdatePromises = currentJobs.map(async (currentJob: ProcessingJob) => {
          try {
            const response = await fetch(`/api/document_job_summary/${currentJob.job_id}`);
            const data = (await response.json()) as FastApiJobSummaryResponse;

            if (
              data.changed &&
              normalizeStatus(data.status) !== normalizeStatus(currentJob.status)
            ) {
              await fetch("/api/revert-job-status", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  jobId: currentJob.job_id,
                  status: data.status,
                  progress_percentage: data.progress_percentage,
                  error_message: data.error_message,
                  optimization_quality_score: data.optimization_quality_score,
                  total_input_tokens: data.total_input_tokens,
                  total_output_tokens: data.total_output_tokens,
                  last_processed: data.last_processed,
                  last_optimized: data.last_optimized,
                  generation_flags: data.generation_flags,
                }),
              });
            }
            return data;
          } catch (error) {
            console.error(
              `Error updating status for job ${currentJob.job_id}:`,
              safeStringify(error),
            );
            return null;
          }
        });

        await Promise.all(statusUpdatePromises);

        const fileUpdatePromises = currentJobs.map(async (currentJob: ProcessingJob) => {
          try {
            await mutate(
              `/api/get_processed_files/${encodeURIComponent(currentJob.job_id)}?checkLatest=true`,
            );
          } catch (error: any) {
            const isNotModified = error?.status === 304;
            if (isNotModified) {
              console.log(`No changes for files of job ${currentJob.job_id}`);
            } else {
              console.error(
                `Error fetching files for job ${currentJob.job_id}:`,
                safeStringify(error),
              );
            }
          }
        });

        await Promise.all(fileUpdatePromises);

        const res = await fetch("/api/process_additional_documents", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            documentId: job.document_id,
            jobId: job.job_id,
          }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Failed to process additional documents");
        }

        setGenerationSuccessful(true);
        await mutateJobSummary();

        useAppStore.setState((state) => ({
          ...state,
          additionalGeneration: {
            ...state.additionalGeneration,
            isGeneratingAdditional: false,
            additionalGenerationProgress: 100,
            additionalGenerationStatus: "completed",
            showResults: true,
            keepModalOpen: true,
          },
        }));
      } catch (error) {
        console.error("Error processing generated files:", safeStringify(error));
        notify.error({
          content: error instanceof Error ? error.message : "Failed to process generated files",
        });

        useAppStore.setState((state) => ({
          ...state,
          additionalGeneration: {
            ...state.additionalGeneration,
            isGeneratingAdditional: false,
            additionalGenerationError:
              error instanceof Error ? error.message : "Failed to process generated files",
            additionalGenerationStatus: "failed",
          },
        }));
      } finally {
        setProcessingFiles(false);
      }
    },
    [job.document_id, job.job_id, mutateJobSummary],
  );

  const renderPresentationThemeSelector = useCallback(
    () => (
      <div className="mt-8">
        <Text strong className="block mb-4 text-gray-700">
          Presentation Theme:
        </Text>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
          {presentationThemes.map((theme) => (
            <div
              key={theme.name}
              className={`cursor-pointer border rounded-lg overflow-hidden relative ${
                presentationTheme === theme.name
                  ? "border-indigo-500 shadow-lg"
                  : "border-gray-300 hover:border-indigo-300"
              } transition-transform transform hover:scale-105 p-2`}
              onClick={() => setPresentationTheme(theme.name)}
              role="button"
              aria-pressed={presentationTheme === theme.name}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setPresentationTheme(theme.name);
                }
              }}
            >
              <div className="aspect-w-1 aspect-h-1">
                <Image
                  src={theme.imgSrc}
                  alt={theme.label}
                  width={128}
                  height={128}
                  className="rounded-md w-full h-full object-contain"
                />
              </div>
              <div className="mt-2 text-center">
                <Text strong className="text-sm sm:text-base">
                  {theme.label}
                </Text>
              </div>

              {presentationTheme === theme.name && (
                <div className="absolute top-1 right-1 bg-indigo-500 text-white rounded-full p-0.5">
                  <CheckOutlined className="text-xs" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    ),
    [presentationThemes, presentationTheme],
  );

  const anyOptionEnabled = Object.entries(options).some(
    ([key, value]) => value && !existingDocumentTypes.includes(key as DocumentOptionKey),
  );

  const renderOption = (option: DocumentOption) => {
    try {
      if (!Array.isArray(existingDocumentTypes)) {
        console.error(
          "existingDocumentTypes is not an array in renderOption:",
          existingDocumentTypes,
        );
        return null;
      }

      const isGenerated = existingDocumentTypes.includes(option.key);

      return (
        <Tooltip title={option.description} key={option.key}>
          <div
            className={`p-4 rounded-lg border ${isGenerated ? "bg-gray-100 cursor-not-allowed" : "hover:border-blue-500 cursor-pointer"}`}
            onClick={() => !isGenerated && handleOptionChange(option.key, !options[option.key])}
          >
            <div className="flex items-center space-x-3">
              <option.icon className={`h-5 w-5 ${isGenerated ? "text-gray-400" : option.color}`} />
              <div className="flex-1">
                <p className="font-medium">{option.title}</p>
                {isGenerated && <p className="text-sm text-gray-500">(Already Generated)</p>}
              </div>
              <Checkbox
                checked={isGenerated || options[option.key]}
                disabled={isGenerated}
                onChange={(e) => handleOptionChange(option.key, e.target.checked)}
                className="h-4 w-4"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        </Tooltip>
      );
    } catch (err) {
      console.error("Error in renderOption:", safeStringify(err));
      return null;
    }
  };

  const renderIntegratedProgressTracker = () => {
    const currentStageIndex = stages.findIndex(
      (stage) => stage.status === additionalGenerationStatus,
    );
    const isFailedStatus = (s: string) =>
      s === "processing_failed" ||
      s.endsWith("_failed") ||
      s.includes("generation_failed") ||
      s === "failed";
    const trulyCompleted = additionalGenerationStatus === "completed" && generationSuccessful;

    return (
      <motion.div
        ref={progressTrackerRef}
        className="progress-tracker-section relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="mt-4 shadow-lg rounded-lg bg-white">
          <Title level={5} className="mb-3 text-center">
            Processing Additional Documents
          </Title>

          <div className="mb-4">
            <Progress
              percent={additionalGenerationProgress}
              status={
                isFailedStatus(additionalGenerationStatus)
                  ? "exception"
                  : additionalGenerationStatus === "completed" && generationSuccessful
                    ? "success"
                    : "active"
              }
              strokeColor={{
                "0%": "#108ee9",
                "100%": "#87d068",
              }}
              size="small"
            />
          </div>

          <Steps
            current={currentStageIndex}
            direction="vertical"
            className="mb-4"
            size="small"
            items={stages.map((stage, index) => {
              let stepStatus: "wait" | "process" | "finish" | "error" = "wait";
              let iconElem = getIconForType(stage.iconType);
              let titleColor = "text-gray-500";

              if (isFailedStatus(stage.status)) {
                stepStatus = "error";
                iconElem = <CloseCircleOutlined style={{ color: "red" }} />;
              } else if (stage.status === additionalGenerationStatus) {
                stepStatus = "process";
                iconElem = <LoadingOutlined style={{ color: "#1890ff" }} />;
                titleColor = "text-black";
              } else if (stage.status === "completed") {
                const isFinalStage = index === stages.length - 1;
                if (isFinalStage) {
                  if (trulyCompleted) {
                    stepStatus = "finish";
                    iconElem = <CheckCircleOutlined style={{ color: "green" }} />;
                    titleColor = "text-black";
                  } else {
                    stepStatus = "wait";
                    iconElem = getIconForType(stage.iconType);
                  }
                } else {
                  stepStatus = "finish";
                  iconElem = <CheckCircleOutlined style={{ color: "green" }} />;
                  titleColor = "text-black";
                }
              }

              return {
                key: stage.title,
                title: <span className={`text-sm ${titleColor}`}>{stage.title}</span>,
                description:
                  stage.status === additionalGenerationStatus ? (
                    <span className="text-xs">{statusDescription}</span>
                  ) : (
                    ""
                  ),
                status: stepStatus,
                icon: iconElem,
              };
            })}
          />

          <Row gutter={[16, 16]} className="mb-3">
            {optimization_qualityScore !== null && (
              <Col xs={24} sm={8}>
                <Tooltip title="Quality score of the generated content">
                  <Statistic
                    title="Quality Score"
                    value={optimization_qualityScore.toFixed(2)}
                    suffix="/ 100"
                    prefix={<StarOutlined />}
                  />
                </Tooltip>
              </Col>
            )}

            {inputTokens !== null && (
              <Col xs={24} sm={8}>
                <Tooltip title="Total input tokens processed">
                  <Statistic
                    title="Input Tokens"
                    value={inputTokens}
                    prefix={<FileTextOutlined />}
                  />
                </Tooltip>
              </Col>
            )}

            {outputTokens !== null && (
              <Col xs={24} sm={8}>
                <Tooltip title="Total output tokens generated">
                  <Statistic
                    title="Output Tokens"
                    value={outputTokens}
                    prefix={<FileTextOutlined />}
                  />
                </Tooltip>
              </Col>
            )}
          </Row>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
            {lastProcessed && (
              <div>
                <Text strong>Last Processed:</Text>
                <br />
                <Text>{new Date(lastProcessed).toLocaleString()}</Text>
              </div>
            )}
            {lastOptimized && (
              <div>
                <Text strong>Last Optimized:</Text>
                <br />
                <Text>{new Date(lastOptimized).toLocaleString()}</Text>
              </div>
            )}
          </div>
        </Card>
      </motion.div>
    );
  };

  return (
    <Modal
      title={
        <div className="mb-2">
          <Title level={4} className="flex items-center text-base sm:text-lg xs:text-sm">
            {!isGeneratingAdditional && !generationSuccessful
              ? "Select Additional Documents"
              : "Processing Additional Documents"}
          </Title>
        </div>
      }
      open={true}
      onCancel={loading || processingFiles ? undefined : onClose}
      footer={null}
      width="80%"
      style={{ maxWidth: "2000px" }}
      styles={{
        body: {
          maxHeight: "85vh",
          overflowX: "hidden",
          overflowY: "auto",
          scrollBehavior: "smooth",
        },
      }}
      className="additional-docs-modal"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="space-y-6">
            <div className={isGeneratingAdditional ? "opacity-50" : ""}>
              {(!generationSuccessful || !isGeneratingAdditional) && (
                <>
                  <div className="mt-6 flex justify-end space-x-4">
                    <Button onClick={onClose} disabled={loading || processingFiles}>
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      onClick={handleSubmit}
                      disabled={!anyOptionEnabled || loading}
                      loading={loading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {loading ? (
                        <div className="flex items-center">
                          <Loader2Icon className="animate-spin mr-2" />
                          Processing...
                        </div>
                      ) : (
                        "Start Processing"
                      )}
                    </Button>
                  </div>

                  <div className="mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {documentOptions.map((option) => renderOption(option))}
                    </div>

                    {options.generatePresentationSlides &&
                      !existingDocumentTypes.includes("generatePresentationSlides") &&
                      renderPresentationThemeSelector()}

                    {options.generateLessonPlan &&
                      !existingDocumentTypes.includes("generateLessonPlan") && (
                        <AudienceSelector
                          lessonPlanTargetAudience={lessonPlanTargetAudience}
                          customLessonPlanTargetAudience={customLessonPlanTargetAudience}
                          setLessonPlanTargetAudience={setLessonPlanTargetAudience}
                          setCustomLessonPlanTargetAudience={setCustomLessonPlanTargetAudience}
                          isLoadingDocumentInfo={loading}
                          isProcessingDocument={processingFiles}
                        />
                      )}

                    {creditCost !== null && (
                      <div className="mt-6">
                        <Title level={5} className="mb-4">
                          Cost Breakdown
                        </Title>
                        <CostBreakdownProgress
                          costBreakdown={calculateIncrementalCost()}
                          showBaseCost={false}
                        />
                      </div>
                    )}

                    {!anyOptionEnabled && (
                      <Alert
                        message="Please select at least one document type that hasn't been generated yet."
                        type="warning"
                        className="mt-4"
                      />
                    )}
                  </div>
                </>
              )}
            </div>

            {isGeneratingAdditional && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                key="progress-tracker"
                className={`mt-6 ${generationSuccessful ? "" : "border-t"} pt-6`}
              >
                {renderIntegratedProgressTracker()}
              </motion.div>
            )}

            {showResultsViewer &&
              jobSummaryData &&
              newlyGeneratedDocs &&
              newlyGeneratedDocs.length > 0 && (
                <motion.div
                  className="generation-results-section mt-6 border-t pt-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  key="results-viewer"
                  ref={resultsSectionRef}
                >
                  <Title level={4} className="mb-4">
                    Newly Generated Content:
                  </Title>
                  <ProcessedDocumentResultsViewer
                    job_id={job.job_id}
                    onUpdateFiles={handleFilesGenerated}
                    initialActiveFilename={lastNewlyGeneratedDocFilename || undefined}
                    siderWidth={350}
                  />
                </motion.div>
              )}
          </div>

          <ConfirmationModal
            isOpen={showConfirmation}
            onClose={() => setShowConfirmation(false)}
            onConfirm={handleConfirm}
            creditCost={creditCost}
            userInfo={userInfo}
            onBuyCredits={() => {
              setShowConfirmation(false);
              window.location.href = "/buy-credits";
            }}
            documentInfo={documentInfo}
          />
        </motion.div>
      </AnimatePresence>
    </Modal>
  );
};

export default withErrorBoundary(
  AdditionalDocumentGeneration,
  "AdditionalDocumentGeneration",
  ErrorFallback,
);
